import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { insightsAPI } from '../../api';
import { Spinner } from '../../components/ui';
import {
  Trophy, TrendingUp, TrendingDown, Award, Sparkles, ChevronDown,
  AlertCircle, RefreshCw, Calendar, Activity
} from 'lucide-react';

/**
 * PRProgression — full PR visualisation per exercise.
 *
 * Layout:
 *   1. Hero row: best-lift, biggest-gain, total PRs
 *   2. Exercise picker (combobox of exercises that have at least one PR)
 *   3. Big SVG line chart with gold PR markers (every PR is a visible
 *      spike), hover tooltip on data points, X-axis = date, Y-axis = value
 *   4. Stat panel: current best, total improvement, days since last PR,
 *      avg days between PRs
 *   5. PR history timeline (most recent first) with delta + dates
 */
export const PRProgression = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [days, setDays] = useState(365);
  const [selectedExerciseId, setSelectedExerciseId] = useState(null);

  const load = async (overrideDays) => {
    setLoading(true);
    setError('');
    try {
      const d = overrideDays || days;
      const res = await insightsAPI.getPRProgression(d);
      setData(res.data);
      // Default-select the strongest lift if no selection yet
      if (!selectedExerciseId && res.data?.exercises?.length > 0) {
        setSelectedExerciseId(res.data.exercises[0].exercise_id);
      }
    } catch (err) {
      console.error('Failed to load PR progression:', err);
      setError(t('prProgression.error', { defaultValue: 'Could not load PR data.' }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const selected = data?.exercises?.find((e) => e.exercise_id === selectedExerciseId) || null;

  return (
    <div className="space-y-6" data-testid="pr-progression">
      <PageHeader days={days} setDays={setDays} onRefresh={() => load()} t={t} />

      {error && <ErrorState message={error} onRetry={() => load()} t={t} />}

      {loading && !data ? (
        <div className="flex items-center justify-center min-h-[420px]">
          <Spinner size="lg" />
        </div>
      ) : data?.summary?.empty_state ? (
        <EmptyState t={t} />
      ) : data ? (
        <>
          <HeroRow summary={data.summary} t={t} />

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            <ExercisePicker
              exercises={data.exercises}
              selected={selectedExerciseId}
              onSelect={setSelectedExerciseId}
              t={t}
            />
            {selected ? (
              <div className="lg:col-span-3 space-y-4">
                <PRChartPanel exercise={selected} t={t} />
                <PRStatsPanel exercise={selected} t={t} />
                <PRHistoryList exercise={selected} t={t} />
              </div>
            ) : (
              <div className="lg:col-span-3 rounded-2xl border border-dashed border-[var(--color-border)] bg-[rgba(8,13,11,0.4)] p-12 text-center">
                <p className="text-[var(--color-text-muted)]">
                  {t('prProgression.pickExercise', { defaultValue: 'Pick an exercise on the left to see your progression.' })}
                </p>
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
};

// ===========================================================================
// Sub-components
// ===========================================================================

function PageHeader({ days, setDays, onRefresh, t }) {
  const options = [
    { v: 90, label: '90 d' },
    { v: 180, label: '6 m' },
    { v: 365, label: '1 y' },
    { v: 1825, label: '5 y' },
  ];
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
      <div>
        <span className="hero-kicker">
          {t('prProgression.kicker', { defaultValue: 'Strength' })}
        </span>
        <h1 className="mt-2 font-display text-3xl uppercase text-[var(--color-text)]">
          {t('prProgression.title', { defaultValue: 'PR progression' })}
        </h1>
        <p className="mt-1 text-sm text-[var(--color-text-muted)] max-w-2xl">
          {t('prProgression.subtitle', {
            defaultValue: 'The curve tells the story. Watch your one-rep max climb over time, exercise by exercise.',
          })}
        </p>
      </div>
      <div className="flex items-center gap-3">
        <div
          className="inline-flex p-1 rounded-2xl border border-[var(--color-border)] bg-white/[0.04]"
          role="tablist"
        >
          {options.map((o) => {
            const active = days === o.v;
            return (
              <button
                key={o.v}
                type="button"
                onClick={() => setDays(o.v)}
                className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors ${
                  active
                    ? 'bg-primary-300 text-[#0a1208]'
                    : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
                }`}
                data-testid={`pr-period-${o.v}`}
              >
                {o.label}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={onRefresh}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-2xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] text-sm font-medium hover:bg-white/[0.08] transition-colors"
          data-testid="pr-refresh"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function HeroRow({ summary, t }) {
  const cards = [
    {
      label: t('prProgression.totalPRs', { defaultValue: 'Total PRs' }),
      value: summary.total_prs,
      unit: t('prProgression.inPeriod', { defaultValue: 'in period' }),
      icon: Trophy,
      color: 'primary',
    },
    {
      label: t('prProgression.bestLift', { defaultValue: 'Best lift' }),
      value: summary.best_lift
        ? `${summary.best_lift.value} ${summary.best_lift.unit || ''}`
        : '—',
      unit: summary.best_lift?.exercise_name || t('prProgression.allTime', { defaultValue: 'all-time' }),
      icon: Award,
      color: 'blue',
    },
    {
      label: t('prProgression.biggestGain', { defaultValue: 'Biggest gain' }),
      value: summary.biggest_gain
        ? `+${summary.biggest_gain.value_pct}%`
        : '—',
      unit: summary.biggest_gain?.exercise_name || t('prProgression.vsFirst', { defaultValue: 'vs first' }),
      icon: TrendingUp,
      color: 'amber',
    },
    {
      label: t('prProgression.exercisesWithPRs', { defaultValue: 'Lifts tracked' }),
      value: summary.exercises_with_prs,
      unit: t('prProgression.withHistory', { defaultValue: 'with history' }),
      icon: Activity,
      color: 'purple',
    },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="pr-summary">
      {cards.map((c) => (
        <SummaryCard key={c.label} {...c} />
      ))}
    </div>
  );
}

function SummaryCard({ label, value, unit, icon: Icon, color = 'primary' }) {
  const colorClasses = {
    primary: 'border-[rgba(213,255,123,0.18)] bg-[rgba(213,255,123,0.05)] text-primary-100',
    blue: 'border-[rgba(95,180,255,0.18)] bg-[rgba(95,180,255,0.05)] text-blue-200',
    amber: 'border-[rgba(255,210,125,0.18)] bg-[rgba(255,210,125,0.05)] text-amber-200',
    purple: 'border-[rgba(195,140,255,0.18)] bg-[rgba(195,140,255,0.05)] text-purple-200',
  };
  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[rgba(8,13,11,0.5)] p-4">
      <div className="flex items-start justify-between gap-3">
        <div className={`p-2 rounded-xl border shrink-0 ${colorClasses[color]}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="text-right">
          <div className="font-display text-2xl text-[var(--color-text)] leading-none">{value}</div>
          <div className="mt-1 text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)] truncate max-w-[140px]">
            {unit}
          </div>
        </div>
      </div>
      <div className="mt-3 text-xs text-[var(--color-text-muted)]">{label}</div>
    </div>
  );
}

function ExercisePicker({ exercises, selected, onSelect, t }) {
  return (
    <div
      className="rounded-2xl border border-[var(--color-border)] bg-[rgba(8,13,11,0.5)] p-4"
      data-testid="pr-exercise-picker"
    >
      <span className="hero-kicker">
        {t('prProgression.picker.kicker', { defaultValue: 'Lifts' })}
      </span>
      <h3 className="mt-1 font-display text-lg uppercase text-[var(--color-text)]">
        {t('prProgression.picker.title', { defaultValue: 'Pick an exercise' })}
      </h3>
      <ul className="mt-4 space-y-1">
        {exercises.map((ex) => {
          const isActive = ex.exercise_id === selected;
          const trendUp = ex.total_improvement_pct > 0;
          return (
            <li key={ex.exercise_id}>
              <button
                type="button"
                onClick={() => onSelect(ex.exercise_id)}
                className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-xl border transition-colors ${
                  isActive
                    ? 'border-primary-200/30 bg-primary-200/10'
                    : 'border-[var(--color-border)] bg-white/[0.02] hover:bg-white/[0.05]'
                }`}
                data-testid={`pr-pick-${ex.exercise_id}`}
              >
                <Trophy className={`w-3.5 h-3.5 shrink-0 ${isActive ? 'text-primary-200' : 'text-[var(--color-text-muted)]'}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[var(--color-text)] truncate">{ex.exercise_name}</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
                    {ex.pr_count} {t('prProgression.prsCount', { defaultValue: 'PRs' })}
                  </div>
                </div>
                {trendUp && (
                  <span className="text-xs text-primary-100 font-display shrink-0">
                    +{ex.total_improvement_pct}%
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function PRChartPanel({ exercise, t }) {
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const wrapRef = useRef(null);

  const W = 800;
  const H = 280;
  const padL = 60;
  const padR = 30;
  const padT = 24;
  const padB = 36;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  const points = (exercise.history || []).map((p) => ({
    date: new Date(p.achieved_at),
    value: p.value,
    id: p.id,
  }));
  const values = points.map((p) => p.value);
  const minVal = Math.min(...values, 0);
  const maxVal = Math.max(...values);
  const range = maxVal - minVal || 1;

  const xFor = (i) => padL + (i / Math.max(points.length - 1, 1)) * innerW;
  const yFor = (v) => padT + innerH - ((v - minVal) / range) * innerH;

  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i).toFixed(1)} ${yFor(p.value).toFixed(1)}`)
    .join(' ');
  const areaPath = `${linePath} L ${xFor(points.length - 1).toFixed(1)} ${(padT + innerH).toFixed(1)} L ${xFor(0).toFixed(1)} ${(padT + innerH).toFixed(1)} Z`;

  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[rgba(8,13,11,0.5)] p-6" data-testid="pr-chart-panel">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <span className="hero-kicker">
            {t('prProgression.chart.kicker', { defaultValue: '1RM curve' })}
          </span>
          <h3 className="mt-1 font-display text-xl uppercase text-[var(--color-text)]">
            {exercise.exercise_name}
          </h3>
        </div>
        <div className="text-right">
          <div className="font-display text-3xl text-[var(--color-text)]">
            {exercise.best_value}
            <span className="text-sm text-[var(--color-text-muted)] ml-1">{exercise.unit}</span>
          </div>
          {exercise.total_improvement_pct > 0 && (
            <div className="text-xs text-primary-100 flex items-center justify-end gap-1">
              <TrendingUp className="w-3 h-3" />
              {t('prProgression.chart.gain', {
                defaultValue: `+${exercise.total_improvement_abs}${exercise.unit} (+${exercise.total_improvement_pct}%) vs first`,
              })}
            </div>
          )}
        </div>
      </div>

      <div ref={wrapRef} className="relative" data-testid="pr-chart">
        <svg viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
          <defs>
            <linearGradient id="pr-line-gradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(213, 255, 123, 0.45)" />
              <stop offset="100%" stopColor="rgba(213, 255, 123, 0)" />
            </linearGradient>
          </defs>

          {/* Y-axis grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map((p) => (
            <line
              key={p}
              x1={padL}
              y1={padT + innerH * p}
              x2={W - padR}
              y2={padT + innerH * p}
              stroke="rgba(255, 255, 255, 0.04)"
              strokeWidth="1"
            />
          ))}

          {/* Y-axis labels */}
          {[maxVal, (maxVal + minVal) / 2, minVal].map((v, i) => (
            <text
              key={i}
              x={padL - 8}
              y={padT + (i * innerH / 2) + 4}
              textAnchor="end"
              className="fill-[var(--color-text-muted)]"
              style={{ fontSize: 10 }}
            >
              {v.toFixed(0)}
            </text>
          ))}

          {/* X-axis labels */}
          {points.length > 0 && [0, Math.floor((points.length - 1) / 2), points.length - 1].map((i, idx) => (
            <text
              key={idx}
              x={xFor(i)}
              y={H - padB + 18}
              textAnchor={idx === 0 ? 'start' : idx === 2 ? 'end' : 'middle'}
              className="fill-[var(--color-text-muted)]"
              style={{ fontSize: 10 }}
            >
              {formatShortDate(points[i].date)}
            </text>
          ))}

          {/* Area fill */}
          <path d={areaPath} fill="url(#pr-line-gradient)" />

          {/* Line */}
          <path
            d={linePath}
            fill="none"
            stroke="rgba(213, 255, 123, 0.95)"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* PR markers (gold circles on each PR) */}
          {points.map((p, i) => (
            <g key={p.id || i}>
              {/* Outer ring for PR marker glow */}
              <circle
                cx={xFor(i)}
                cy={yFor(p.value)}
                r={hoveredIdx === i ? 10 : 7}
                fill="rgba(255, 210, 125, 0.15)"
                stroke="rgba(255, 210, 125, 0.4)"
                strokeWidth="1.5"
                style={{ transition: 'r 0.15s ease' }}
              />
              {/* Inner solid dot */}
              <circle
                cx={xFor(i)}
                cy={yFor(p.value)}
                r={hoveredIdx === i ? 6 : 4.5}
                fill="rgba(255, 210, 125, 1)"
                stroke="#0a1208"
                strokeWidth="1.5"
                style={{ transition: 'r 0.15s ease' }}
              />
              {/* Hitbox */}
              <rect
                x={xFor(i) - (innerW / points.length) / 2}
                y={padT}
                width={innerW / points.length}
                height={innerH}
                fill="transparent"
                onMouseEnter={() => setHoveredIdx(i)}
                onMouseLeave={() => setHoveredIdx(null)}
                style={{ cursor: 'pointer' }}
                data-testid={`pr-point-${i}`}
              />
            </g>
          ))}
        </svg>

        {hoveredIdx !== null && (
          <div
            className="absolute pointer-events-none rounded-xl border border-amber-500/40 bg-[#0a1208]/95 backdrop-blur-sm px-3 py-2 shadow-2xl"
            style={{
              left: `${(xFor(hoveredIdx) / W) * 100}%`,
              top: `${(yFor(points[hoveredIdx].value) / H) * 100}%`,
              transform: 'translate(-50%, -130%)',
            }}
            data-testid="pr-tooltip"
          >
            <div className="text-[10px] uppercase tracking-[0.18em] text-amber-200 font-semibold flex items-center gap-1">
              <Trophy className="w-3 h-3" /> PR
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)] mt-1">
              {formatFullDate(points[hoveredIdx].date)}
            </div>
            <div className="font-display text-lg text-[var(--color-text)]">
              {points[hoveredIdx].value}<span className="text-xs text-[var(--color-text-muted)] ml-1">{exercise.unit}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PRStatsPanel({ exercise, t }) {
  const stats = [
    {
      label: t('prProgression.stats.best', { defaultValue: 'Best' }),
      value: `${exercise.best_value} ${exercise.unit}`,
      sub: t('prProgression.stats.allTime', { defaultValue: 'all-time PR' }),
      color: 'amber',
    },
    {
      label: t('prProgression.stats.first', { defaultValue: 'First' }),
      value: `${exercise.first_value} ${exercise.unit}`,
      sub: t('prProgression.stats.startingPoint', { defaultValue: 'starting point' }),
      color: 'muted',
    },
    {
      label: t('prProgression.stats.gain', { defaultValue: 'Total gain' }),
      value: `+${exercise.total_improvement_abs} ${exercise.unit}`,
      sub: `+${exercise.total_improvement_pct}% ${t('prProgression.stats.overall', { defaultValue: 'overall' })}`,
      color: 'primary',
    },
    {
      label: t('prProgression.stats.prs', { defaultValue: 'PRs hit' }),
      value: exercise.pr_count,
      sub: t('prProgression.stats.inRange', { defaultValue: 'in period' }),
      color: 'blue',
    },
    {
      label: t('prProgression.stats.avgGap', { defaultValue: 'Avg between' }),
      value: exercise.avg_days_between_prs > 0 ? `${exercise.avg_days_between_prs}d` : '—',
      sub: t('prProgression.stats.betweenPRs', { defaultValue: 'between PRs' }),
      color: 'purple',
    },
  ];

  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[rgba(8,13,11,0.5)] p-6" data-testid="pr-stats">
      <span className="hero-kicker">
        {t('prProgression.stats.kicker', { defaultValue: 'Numbers' })}
      </span>
      <h3 className="mt-1 font-display text-lg uppercase text-[var(--color-text)] mb-4">
        {t('prProgression.stats.title', { defaultValue: 'At a glance' })}
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {stats.map((s) => (
          <StatTile key={s.label} {...s} />
        ))}
      </div>
    </div>
  );
}

function StatTile({ label, value, sub, color = 'primary' }) {
  const colorClasses = {
    primary: 'border-[rgba(213,255,123,0.18)] bg-[rgba(213,255,123,0.05)] text-primary-100',
    blue: 'border-[rgba(95,180,255,0.18)] bg-[rgba(95,180,255,0.05)] text-blue-200',
    amber: 'border-[rgba(255,210,125,0.18)] bg-[rgba(255,210,125,0.05)] text-amber-200',
    purple: 'border-[rgba(195,140,255,0.18)] bg-[rgba(195,140,255,0.05)] text-purple-200',
    muted: 'border-[var(--color-border)] bg-white/[0.02] text-[var(--color-text-muted)]',
  };
  return (
    <div className={`rounded-2xl border p-3 ${colorClasses[color]}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] opacity-70 truncate">
        {label}
      </div>
      <div className="font-display text-2xl text-[var(--color-text)] mt-1 leading-none">
        {value}
      </div>
      <div className="text-[10px] text-[var(--color-text-muted)] mt-1 truncate">
        {sub}
      </div>
    </div>
  );
}

function PRHistoryList({ exercise, t }) {
  // Most recent first
  const sorted = [...(exercise.history || [])].sort(
    (a, b) => new Date(b.achieved_at) - new Date(a.achieved_at)
  );
  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[rgba(8,13,11,0.5)] p-6" data-testid="pr-history">
      <div className="flex items-center justify-between mb-4">
        <div>
          <span className="hero-kicker">
            {t('prProgression.history.kicker', { defaultValue: 'Timeline' })}
          </span>
          <h3 className="mt-1 font-display text-lg uppercase text-[var(--color-text)]">
            {t('prProgression.history.title', { defaultValue: 'PR history' })}
          </h3>
        </div>
        <Sparkles className="w-5 h-5 text-primary-200" />
      </div>
      <ul className="space-y-2">
        {sorted.map((p, idx) => {
          const improvement = p.improvement ?? 0;
          const isFirst = idx === sorted.length - 1;
          return (
            <li
              key={p.id}
              className="flex items-center gap-3 px-3 py-2.5 rounded-2xl border border-[var(--color-border)] bg-white/[0.02]"
              data-testid={`pr-history-row-${p.id}`}
            >
              <div className="shrink-0 h-8 w-8 rounded-full bg-gradient-to-br from-amber-500/30 to-amber-200/20 border border-amber-200/30 flex items-center justify-center">
                <Trophy className="w-4 h-4 text-amber-200" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[var(--color-text)]">
                  <span className="font-display text-base">
                    {p.value} <span className="text-xs text-[var(--color-text-muted)]">{p.unit}</span>
                  </span>
                  {isFirst ? (
                    <span className="ml-2 text-[10px] uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
                      {t('prProgression.history.first', { defaultValue: 'First PR' })}
                    </span>
                  ) : improvement > 0 ? (
                    <span className="ml-2 text-[10px] uppercase tracking-[0.18em] text-primary-100">
                      +{improvement.toFixed(1)}%
                    </span>
                  ) : null}
                </div>
                <div className="text-[10px] text-[var(--color-text-muted)]">
                  {formatFullDate(new Date(p.achieved_at))}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function EmptyState({ t }) {
  return (
    <div className="rounded-2xl border border-dashed border-[var(--color-border)] bg-[rgba(8,13,11,0.4)] p-12 text-center">
      <Trophy className="w-8 h-8 text-[var(--color-text-muted)] mx-auto mb-3" />
      <p className="font-display text-lg text-[var(--color-text)]">
        {t('prProgression.emptyTitle', { defaultValue: 'No PRs yet' })}
      </p>
      <p className="mt-1 text-sm text-[var(--color-text-muted)] max-w-md mx-auto">
        {t('prProgression.emptyHint', {
          defaultValue: 'Log a workout with a personal-record weight and your progression will start here.',
        })}
      </p>
    </div>
  );
}

function ErrorState({ message, onRetry, t }) {
  return (
    <div className="rounded-2xl border border-red-500/30 bg-red-500/10 text-red-200 p-4 flex items-center gap-3">
      <AlertCircle className="w-5 h-5 shrink-0" />
      <p className="text-sm flex-1">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-[var(--color-border)] bg-white/[0.04] text-[var(--color-text)] text-xs font-semibold hover:bg-white/[0.08] transition-colors"
      >
        <RefreshCw className="w-3.5 h-3.5" />
        {t('prProgression.retry', { defaultValue: 'Retry' })}
      </button>
    </div>
  );
}

// ===========================================================================
// Helpers
// ===========================================================================

function formatShortDate(d) {
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatFullDate(d) {
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default PRProgression;
